import { Injectable, inject } from '@angular/core';
import { ParamMap, PRIMARY_OUTLET, Router, UrlTree } from '@angular/router';
import { SnackbarService } from '@nrwl/snackbar-services';

export interface PlanCustomizationOrigin {
  source: 'inbox' | 'client-record';
  returnUrl: UrlTree;
}

@Injectable({ providedIn: 'root' })
export class PlanCustomizationNavigationService {
  private readonly router = inject(Router);
  private readonly snackbarService = inject(SnackbarService);
  private personalizedPlansStale = false;

  /** Opens customization from a plan menu, preserving the origin across refresh. */
  async open(instanceId: number): Promise<boolean> {
    if (!Number.isSafeInteger(instanceId) || instanceId <= 0) {
      this.snackbarService.error('Unable to customize this plan: invalid plan ID.');
      return false;
    }

    const origin = this.parseOrigin(this.router.url);
    if (!origin) {
      this.snackbarService.error(
        'Open plan customization from Personalized Plans or a client record.',
      );
      return false;
    }

    if (origin.source === 'inbox') {
      origin.returnUrl.queryParams = {
        ...origin.returnUrl.queryParams,
        formsTab: 'personalized-plans',
      };
    } else if (origin.returnUrl.root.children[PRIMARY_OUTLET]?.segments[1]?.path === 'library') {
      origin.returnUrl.queryParams = {
        ...origin.returnUrl.queryParams,
        form: instanceId,
      };
    }

    return this.router.navigate(['/personalized-plans', instanceId, 'customize'], {
      queryParams: {
        origin: origin.source,
        returnUrl: this.router.serializeUrl(origin.returnUrl),
      },
    });
  }

  getOrigin(params: ParamMap): PlanCustomizationOrigin | null {
    const source = params.get('origin');
    const returnUrl = params.get('returnUrl');

    // Direct links without entry context return to the Personalized Plans tab.
    if (source === null && returnUrl === null) {
      return {
        source: 'inbox',
        returnUrl: this.router.createUrlTree(['/inbox'], {
          queryParams: { formsTab: 'personalized-plans' },
        }),
      };
    }

    if (!returnUrl) {
      return null;
    }

    const origin = this.parseOrigin(returnUrl);
    return origin?.source === source ? origin : null;
  }

  navigateToOrigin(params: ParamMap): Promise<boolean> {
    const origin = this.getOrigin(params);
    if (!origin) {
      this.snackbarService.error('Unable to restore the originating screen.');
      return this.router.navigate(['/inbox'], { replaceUrl: true });
    }

    return this.router.navigateByUrl(origin.returnUrl, { replaceUrl: true });
  }

  markPersonalizedPlansStale(): void {
    this.personalizedPlansStale = true;
  }

  consumePersonalizedPlansStale(): boolean {
    const stale = this.personalizedPlansStale;
    this.personalizedPlansStale = false;
    return stale;
  }

  navigateToClientRecord(params: ParamMap): Promise<boolean> {
    const origin = this.getOrigin(params);
    const segments = origin?.returnUrl.root.children[PRIMARY_OUTLET]?.segments ?? [];
    const patientId = Number(segments[segments.length - 1]?.path);
    if (origin?.source !== 'client-record' || !Number.isSafeInteger(patientId) || patientId <= 0) {
      this.snackbarService.error('Unable to return to the client record.');
      return Promise.resolve(false);
    }

    return this.router.navigate(['/patient', patientId], {
      queryParams:
        origin.returnUrl.queryParams['archived'] === 'true' ? { archived: true } : undefined,
    });
  }

  private parseOrigin(returnUrl: string): PlanCustomizationOrigin | null {
    if (!returnUrl.startsWith('/') || returnUrl.startsWith('//')) {
      return null;
    }

    const url = this.router.parseUrl(returnUrl);
    const primary = url.root.children[PRIMARY_OUTLET];
    if (
      !primary ||
      Object.keys(url.root.children).length !== 1 ||
      Object.keys(primary.children).length > 0 ||
      primary.segments.some((segment) => Object.keys(segment.parameters).length > 0)
    ) {
      return null;
    }

    const path = '/' + primary.segments.map((segment) => segment.path).join('/');
    if (path === '/inbox') {
      return { source: 'inbox', returnUrl: url };
    }

    const patientMatch = path.match(
      /^\/(?:patient|clients\/(?:library|patient-record))\/([1-9]\d*)$/,
    );
    if (patientMatch && Number.isSafeInteger(Number(patientMatch[1]))) {
      return { source: 'client-record', returnUrl: url };
    }

    return null;
  }
}
